import { defineStore } from "pinia";
import { SessionNotificationsApi, type SessionNotificationItem } from "@simbelapp/order-sdk";
import type { ISessionDetailsNotificationsStore } from "~/utils/interfaces/sessions-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { notificationDetailRequest } from "~/utils/helpers/session-notifications/session-notification-display";

/** Bumped by each list request and when the table closes: an answer landing after either is dropped. */
let listRequestSeq = 0;
/** Same for the detail: an earlier click answering last must neither replace nor reopen the newer one. */
let detailRequestSeq = 0;

export const useSessionDetailsNotificationsStore = defineStore("useSessionDetailsNotificationsStore", {
  state: (): ISessionDetailsNotificationsStore => {
    return {
      sessionNotifications: null,
      currentNotification: null,
      showNotificationViewModal: false,
    };
  },
  getters: {},
  actions: {
    /**
     * One page of the session's unified list, date descending. Orders honours `offset`, `limit` and
     * `search`, which matches the subject only (AC-ENVOI.8); sort is not offered. Returns whether it loaded.
     */
    async fetchSessionNotifications(
      sessionId: string,
      page: { offset: number; limit: number; search?: string | null },
    ): Promise<boolean> {
      const requestSeq = ++listRequestSeq;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionNotificationsApi);
        const list = await apiInstance.getSessionNotifications({
          sessionId,
          offset: String(page.offset),
          limit: String(page.limit),
          search: page.search?.trim() || undefined,
        });
        // A page asked for by a table closed since, or superseded by a newer one, must not replace the rows.
        if (requestSeq === listRequestSeq) this.sessionNotifications = list;
        return true;
      } catch (error) {
        console.error(error);
        if (requestSeq === listRequestSeq) this.sessionNotifications = null;
        return false;
      }
    },
    /** The detail behind a row's eye; a rule step not fired yet comes back rendered live. */
    async fetchNotificationDetail(entry: SessionNotificationItem): Promise<boolean> {
      const feedback = useFeedback();
      const requestSeq = ++detailRequestSeq;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionNotificationsApi);
        const detail = await apiInstance.getSessionNotificationDetail({
          getSessionNotificationDetailDto: notificationDetailRequest(entry),
        });
        if (requestSeq !== detailRequestSeq) return false;
        this.currentNotification = detail;
        return true;
      } catch (error) {
        console.error(error);
        if (requestSeq !== detailRequestSeq) return false;
        this.currentNotification = null;
        feedback.error("Impossible d'afficher le détail de cette notification", "small");
        return false;
      }
    },
    /** The table closed: an answer still in flight must not land, nor reopen its detail over the next session. */
    supersedePendingRequests() {
      listRequestSeq++;
      detailRequestSeq++;
    },
  },
});
